import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Important for local network access on mobile
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthLogin = requestUrl.includes('/auth/login');
    
    // Log error details for debugging
    if (error.message === 'Network Error' || error.code === 'CORS') {
      console.warn('Network/CORS error detected:', {
        message: error.message,
        code: error.code,
        url: error.config?.url,
        status: error.response?.status
      });
    }
    
    if (error.response?.status === 401 && !isAuthLogin) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  refreshToken: async (refreshToken) => {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
  
  changePassword: async (passwordData) => {
    const response = await api.post('/auth/change-password', passwordData);
    return response.data;
  },
};

// Calendar API
export const calendarAPI = {
  getHolidays: async (params = {}) => {
    const response = await api.get('/calendar/holidays', { params });
    return response.data;
  },
  
  createHoliday: async (holidayData) => {
    const response = await api.post('/calendar/holidays', holidayData);
    return response.data;
  },
  
  updateHoliday: async (id, updateData) => {
    const response = await api.put(`/calendar/holidays/${id}`, updateData);
    return response.data;
  },
  
  deleteHoliday: async (id) => {
    const response = await api.delete(`/calendar/holidays/${id}`);
    return response.data;
  },
  
  getCurrentDate: async () => {
    const response = await api.get('/calendar/current-date');
    return response.data;
  },
  
  getPredefinedHolidays: async (year) => {
    const response = await api.get(`/calendar/predefined-holidays/${year}`);
    return response.data;
  },
};

// Activities API
export const activitiesAPI = {
  getActivities: async (params = {}) => {
    const response = await api.get('/activities/', { params });
    return response.data;
  },
};

// Staff Attendance API
export const staffAttendanceAPI = {
  clockIn: async () => {
    const response = await api.post('/staff-attendance/clock-in');
    return response.data;
  },
  
  clockOut: async () => {
    const response = await api.post('/staff-attendance/clock-out');
    return response.data;
  },
  
  getAttendance: async (params = {}) => {
    const response = await api.get('/staff-attendance/my-history', { params });
    return response.data;
  },
  
  getTodayAttendance: async () => {
    const response = await api.get('/staff-attendance/today-status');
    return response.data;
  },
  
  markAttendance: async (attendanceData) => {
    // This endpoint doesn't exist in backend, use clock-in/clock-out instead
    if (attendanceData.status === 'present') {
      return await staffAttendanceAPI.clockIn();
    }
    throw new Error('Manual attendance marking not supported. Use clock-in/clock-out.');
  },
};

// Meal Requests API
export const mealRequestsAPI = {
  createRequest: async (requestData) => {
    const response = await api.post('/meals/requests', requestData);
    return response.data;
  },
  
  getRequests: async (params = {}) => {
    const response = await api.get('/meals/requests-with-school-info', { params });
    return response.data;
  },
  
  updateRequest: async (id, updateData) => {
    const response = await api.put(`/meals/requests/${id}/status`, updateData);
    return response.data;
  },
  
  approveRequest: async (id) => {
    const response = await api.put(`/meals/requests/${id}/status`, { status: 'acknowledged' });
    return response.data;
  },
  
  rejectRequest: async (id, reason) => {
    const response = await api.put(`/meals/requests/${id}/status`, { status: 'cancelled', remarks: reason });
    return response.data;
  },
  
  getMyRequests: async () => {
    // This endpoint doesn't exist, use getRequests with user filter
    const response = await api.get('/meals/requests');
    return response.data;
  },
};

// School Meal Cost API
export const schoolMealCostAPI = {
  createMealCost: async (costData) => {
    const response = await api.post('/school-meal-costs/', costData);
    return response.data;
  },
  
  getMealCosts: async (schoolId) => {
    const response = await api.get(`/school-meal-costs/${schoolId}`);
    return response.data;
  },
  
  updateMealCost: async (id, updateData) => {
    const response = await api.put(`/school-meal-costs/${id}`, updateData);
    return response.data;
  },
};

// Branches API
export const branchesAPI = {
  getBranches: async (params = {}) => {
    const response = await api.get('/branches/', { params });
    return response.data;
  },
  
  getBranch: async (id) => {
    const response = await api.get(`/branches/${id}`);
    return response.data;
  },
  
  createBranch: async (branchData) => {
    const response = await api.post('/branches/', branchData);
    return response.data;
  },
  
  updateBranch: async (id, updateData) => {
    const response = await api.put(`/branches/${id}`, updateData);
    return response.data;
  },
  
  deleteBranch: async (id) => {
    const response = await api.delete(`/branches/${id}`);
    return response.data;
  },
  
  getBranchSchools: async (branchId) => {
    const response = await api.get(`/branches/${branchId}/schools`);
    return response.data;
  },
};

// Schools API
export const schoolsAPI = {
  getSchools: async (params = {}) => {
    const response = await api.get('/branches/schools', { params });
    return response.data;
  },
  
  getSchool: async (id) => {
    const response = await api.get(`/branches/schools/${id}`);
    return response.data;
  },
  
  createSchool: async (schoolData) => {
    const response = await api.post('/branches/schools', schoolData);
    return response.data;
  },
  
  updateSchool: async (id, updateData) => {
    const response = await api.put(`/branches/schools/${id}`, updateData);
    return response.data;
  },
  
  deleteSchool: async (id) => {
    const response = await api.delete(`/branches/schools/${id}`);
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/users/', { params });
    return response.data;
  },
  
  getUser: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  
  createUser: async (userData) => {
    const response = await api.post('/users/', userData);
    return response.data;
  },
  
  updateUser: async (id, updateData) => {
    const response = await api.put(`/users/${id}`, updateData);
    return response.data;
  },
  
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

// Lookup Data API
export const lookupDataAPI = {
  getLookupData: async () => {
    const response = await api.get('/lookup-data/');
    return response.data;
  },
  
  getDashboardStats: async () => {
    const response = await api.get('/lookup-data/');
    return response.data.dashboard_stats || {};
  },
  
  getBranches: async () => {
    const response = await api.post('/lookup-data/category', { category: 'branch', detail: true });
    return { branches: response.data.data };
  },
  
  getSchools: async (branchId = null) => {
    const response = await api.post('/lookup-data/category', { category: 'school', detail: true });
    let schools = response.data.data;
    if (branchId) {
      schools = schools.filter(school => school.branch_id === branchId);
    }
    return { schools };
  },
  
  getUsers: async (branchId = null, schoolId = null) => {
    const response = await api.post('/lookup-data/category', { 
      category: 'user',
      detail: true
    });
    let users = response.data.data || [];
    if (branchId) {
      users = users.filter(user => user.branch_id === branchId);
    }
    if (schoolId) {
      users = users.filter(user => user.school_id === schoolId);
    }
    return { users };
  },
};

// Reports API
export const reportsAPI = {
  // Attendance Reports
  getAttendanceSummary: async (params = {}) => {
    const response = await api.get('/reports/attendance-summary', { params });
    return response.data;
  },
  
  exportAttendanceExcel: async (params = {}) => {
    const response = await api.get('/reports/attendance-excel', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Meal Reports
  getMealVariance: async (params = {}) => {
    const response = await api.get('/reports/meal-variance', { params });
    return response.data;
  },
  
  exportMealVariancePDF: async (params = {}) => {
    const response = await api.get('/reports/meal-variance-pdf', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Meal Requests Reports
  getMealRequests: async (params = {}) => {
    const response = await api.get('/reports/meal-requests', { params });
    return response.data;
  },
  
  exportMealRequestsPDF: async (params = {}) => {
    const response = await api.get('/reports/meal-requests-pdf', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
  
  // School Invoice Reports
  getSchoolInvoice: async (schoolId, month) => {
    const response = await api.get(`/reports/school-invoice/${schoolId}/${month}`);
    return response.data;
  },
  
  exportSchoolInvoicePDF: async (schoolId, month) => {
    const response = await api.get(`/reports/school-invoice-pdf/${schoolId}/${month}`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Employee Attendance Reports
  getEmployeeAttendance: async (params = {}) => {
    const response = await api.get('/reports/employee-attendance', { params });
    return response.data;
  },
  
  exportEmployeeAttendanceExcel: async (params = {}) => {
    const response = await api.get('/reports/employee-attendance-excel', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  }
};

// Roles API
export const rolesAPI = {
  getRoles: async () => {
    const response = await api.get('/roles/');
    return response.data;
  },
  
  getRole: async (id) => {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  },
  
  createRole: async (roleData) => {
    const response = await api.post('/roles/', roleData);
    return response.data;
  },
  
  updateRole: async (id, updateData) => {
    const response = await api.put(`/roles/${id}`, updateData);
    return response.data;
  },
  
  deleteRole: async (id) => {
    const response = await api.delete(`/roles/${id}`);
    return response.data;
  },
};

// Tasks API
export const tasksAPI = {
  getTasks: async (params = {}) => {
    const response = await api.get('/tasks/', { params });
    return response.data;
  },
  
  getTask: async (id) => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },
  
  createTask: async (taskData) => {
    const response = await api.post('/tasks/', taskData);
    return response.data;
  },
  
  updateTask: async (id, updateData) => {
    const response = await api.put(`/tasks/${id}`, updateData);
    return response.data;
  },
  
  deleteTask: async (id) => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },
  
  // Task status management
  updateTaskStatus: async (id, status) => {
    const response = await api.put(`/tasks/${id}/status`, { status });
    return response.data;
  },
  
  assignTask: async (id, userId) => {
    const response = await api.put(`/tasks/${id}/assign`, { user_id: userId });
    return response.data;
  },
  
  // Task comments
  getTaskComments: async (taskId) => {
    const response = await api.get(`/tasks/${taskId}/comments`);
    return response.data;
  },
  
  addTaskComment: async (taskId, comment) => {
    const response = await api.post(`/tasks/${taskId}/comments`, { comment });
    return response.data;
  },
  
  // Task attachments
  getTaskAttachments: async (taskId) => {
    const response = await api.get(`/tasks/${taskId}/attachments`);
    return response.data;
  },
  
  uploadTaskAttachment: async (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  deleteTaskAttachment: async (taskId, attachmentId) => {
    const response = await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
    return response.data;
  },
  
  // Task filtering and search
  getTasksByStatus: async (status, params = {}) => {
    const response = await api.get(`/tasks/status/${status}`, { params });
    return response.data;
  },
  
  getTasksByUser: async (userId, params = {}) => {
    const response = await api.get(`/tasks/user/${userId}`, { params });
    return response.data;
  },
  
  searchTasks: async (query, params = {}) => {
    const response = await api.get('/tasks/search', { 
      params: { ...params, q: query }
    });
    return response.data;
  },
  
  // Task statistics
  getTaskStats: async (params = {}) => {
    const response = await api.get('/tasks/stats', { params });
    return response.data;
  },
  
  getMyTasks: async (params = {}) => {
    const response = await api.get('/tasks/my', { params });
    return response.data;
  }
};

// Settings API
export const settingsAPI = {
  // System Settings
  getSettings: async () => {
    const response = await api.get('/settings');
    return response.data;
  },
  
  updateSettings: async (settingsData) => {
    const response = await api.put('/settings', settingsData);
    return response.data;
  },
  
  // Office Hours Settings
  getOfficeHours: async () => {
    const response = await api.get('/settings/office-hours');
    return response.data;
  },
  
  updateOfficeHours: async (officeHoursData) => {
    const response = await api.put('/settings/office-hours', officeHoursData);
    return response.data;
  },
  
  getOfficeHoursPublic: async () => {
    const response = await api.get('/settings/office-hours/public');
    return response.data;
  },
  
  // Password Policy Settings
  getPasswordPolicy: async () => {
    const response = await api.get('/settings/password-policy');
    return response.data;
  },
  
  updatePasswordPolicy: async (passwordPolicyData) => {
    const response = await api.put('/settings/password-policy', passwordPolicyData);
    return response.data;
  },
  
  // User Profile Settings
  getProfile: async () => {
    const response = await api.get('/settings/profile');
    return response.data;
  },
  
  updateProfile: async (profileData) => {
    const response = await api.put('/settings/profile', profileData);
    return response.data;
  },
  
  // Password Management
  changePassword: async (passwordData) => {
    const response = await api.post('/settings/change-password', passwordData);
    return response.data;
  },
  
  // Backup and Restore
  createBackup: async () => {
    const response = await api.post('/settings/backup');
    return response.data;
  },
  
  getBackups: async () => {
    const response = await api.get('/settings/backups');
    return response.data;
  },
  
  restoreBackup: async (backupId) => {
    const response = await api.post(`/settings/restore/${backupId}`);
    return response.data;
  },
  
  // System Configuration
  getSystemConfig: async () => {
    const response = await api.get('/settings/system');
    return response.data;
  },
  
  updateSystemConfig: async (configData) => {
    const response = await api.put('/settings/system', configData);
    return response.data;
  },
  
  // Email Settings
  getEmailSettings: async () => {
    const response = await api.get('/settings/email');
    return response.data;
  },
  
  updateEmailSettings: async (emailData) => {
    const response = await api.put('/settings/email', emailData);
    return response.data;
  },
  
  testEmailSettings: async () => {
    const response = await api.post('/settings/email/test');
    return response.data;
  }
};
